import { getCurrentClient, getCurrentEnv } from '../../config';

export enum Clients {
  Sightx = 'sightx',
  Australia = 'australia',
  Europe = 'EUROPE_UNION',
  Netquest = 'netquest',
  Pureprofile = 'pureprofile',
  Meta = 'meta',
}

export enum Features {
  FEATURE0 = 'feature0',
  FEATURE1 = 'feature1',
  /** @deprecated */
  catalogForRank = 'catalogForRank',
  /** @deprecated */
  panel = 'panel',
  /** @deprecated */
  panel2 = 'panel2',
  /** @deprecated */
  titleHyperlink = 'titleHyperlink',
  /** @deprecated */
  videoQuestionMVP = 'video-question-mvp',
  recaptchaPosition = 'recaptchaPosition',
  /** @deprecated */
  promoteVideoQuestion = 'promoteVideoQuestion',
  /** @deprecated */
  contactInfoV2 = 'contactInfoV2',
  /** @deprecated */
  communityBasedPiping = 'community_based_piping',
  /** @deprecated */
  gridDropdown = 'grid_dropdown',
  /** @deprecated */
  meta = 'meta',
  /** @deprecated */
  thankYouPageUpdates = 'thank_you_page_updates',
  /** @deprecated */
  metaAI = 'meta_ai',
  /** @deprecated */
  redemPhase2 = 'redem_phase2',
  /** @deprecated */
  populateWithEnhancements = 'populate_with_enhancements',
  /** @deprecated */
  numericLabels = 'numeric_labels',
  /** @deprecated */
  gridCarousel = 'grid_carousel',
  /** @deprecated */
  displayGroupLabels = 'display-group-labels-and-others',
  /** @deprecated */
  metaAI2 = 'meta_ai2',
  metaProgressBar = 'metaProgressBar',
  /** @deprecated */
  redemPhase3Epic1 = 'redemPhase3Epic1',
  redemPhase3Epic3_4 = 'redemPhase3Epic3_4',
  defaultAnswerValue = 'default-answer-value',
}

export enum DynamicEnvironments {
  /** @deprecated */
  catalogForRank = 'catalogForRank',
  testEnv = 'testEnv', // ONLY FOR TESTING PURPOSES
  /** @deprecated */
  panel = 'panel',
  /** @deprecated */
  panel2 = 'panel2',
  Conjoint = 'conjoint',
  Dev01 = 'dev01',
  Meta = 'meta',
}

export enum Environments {
  prod = 'prod',
  staging = 'staging',
  litestaging = 'litestaging',
}

type FeatureEnable = {
  [feature in Features]: Set<Environments | DynamicEnvironments>;
};

type FeatureEnableInDynamicEnvironments = {
  [feature in Features]: Set<DynamicEnvironments>;
};

// Add dynamic environment here to enable the feature on it
const featureEnablementInDynamicEnvs: FeatureEnableInDynamicEnvironments = {
  // test features
  [Features.FEATURE0]: new Set([DynamicEnvironments.testEnv]),
  [Features.FEATURE1]: new Set([]),
  [Features.panel]: new Set([]),
  [Features.panel2]: new Set([]),
  [Features.catalogForRank]: new Set([]),
  [Features.titleHyperlink]: new Set([]),
  [Features.videoQuestionMVP]: new Set([]),
  [Features.recaptchaPosition]: new Set([DynamicEnvironments.Dev01]),
  [Features.promoteVideoQuestion]: new Set([DynamicEnvironments.Dev01]),
  [Features.contactInfoV2]: new Set([]),
  [Features.communityBasedPiping]: new Set([]),
  [Features.gridDropdown]: new Set([DynamicEnvironments.Meta]),
  [Features.meta]: new Set([]),
  [Features.metaAI]: new Set([]),
  [Features.thankYouPageUpdates]: new Set([]),
  [Features.redemPhase2]: new Set([]),
  [Features.populateWithEnhancements]: new Set([DynamicEnvironments.Dev01]),
  [Features.numericLabels]: new Set([DynamicEnvironments.Dev01]),
  [Features.gridCarousel]: new Set([]),
  [Features.displayGroupLabels]: new Set([DynamicEnvironments.Dev01]),
  [Features.metaAI2]: new Set([]),
  [Features.metaProgressBar]: new Set([DynamicEnvironments.Meta]),
  [Features.redemPhase3Epic1]: new Set([DynamicEnvironments.Dev01]),
  [Features.redemPhase3Epic3_4]: new Set([DynamicEnvironments.Dev01]),
  [Features.defaultAnswerValue]: new Set([DynamicEnvironments.Dev01]),
};

// Base feature enablement should come from the dynamic environments
const featureEnablement: FeatureEnable = featureEnablementInDynamicEnvs;

// Add feature here to enable it in staging
const featuresDeployedToStaging: Features[] = [];

featuresDeployedToStaging.forEach((feature) => {
  featureEnablement[feature].add(Environments.staging);
  featureEnablement[feature].add(Environments.litestaging);
});

const featuresDisabledProdByClient: {
  [client: string]: Set<Features>;
} = {
  [Clients.Sightx]: new Set<Features>([Features.meta, Features.gridDropdown, Features.metaAI, Features.metaAI2]),
  [Clients.Europe]: new Set<Features>([Features.meta, Features.gridDropdown, Features.metaAI, Features.metaAI2]),
  [Clients.Australia]: new Set<Features>([Features.meta, Features.gridDropdown, Features.metaAI, Features.metaAI2]),
  [Clients.Netquest]: new Set<Features>([
    Features.meta,
    Features.gridDropdown,
    Features.metaAI,
    Features.metaAI2,
    Features.redemPhase2,
  ]),
  [Clients.Pureprofile]: new Set<Features>([
    Features.meta,
    Features.gridDropdown,
    Features.metaAI,
    Features.metaAI2,
    Features.redemPhase2,
  ]),
  [Clients.Meta]: new Set<Features>([Features.redemPhase2]),
};

// Add feature here to enable it in prod
const featuresDeployedToProd = new Set<Features>([
  Features.catalogForRank,
  Features.panel,
  Features.panel2,
  Features.titleHyperlink,
  Features.videoQuestionMVP,
  Features.promoteVideoQuestion,
  Features.communityBasedPiping,
  Features.contactInfoV2,
  Features.meta,
  Features.gridDropdown,
  Features.thankYouPageUpdates,
  Features.metaAI,
  Features.metaAI2,
  Features.redemPhase2,
  Features.populateWithEnhancements,
  Features.numericLabels,
  Features.gridCarousel,
  Features.displayGroupLabels,
  Features.redemPhase3Epic1,
]);

const isDisabledForClientInProd = (feature: Features) => featuresDisabledProdByClient[getCurrentClient()]?.has(feature);

const disableDeployedToProd = (process.env['NODE_ENV'] ?? '') === 'test';
export const isFeatureEnabled = disableDeployedToProd
  ? (feature: Features) =>
      feature === getCurrentEnv() || (featureEnablement[feature]?.has(getCurrentEnv() as Environments) ?? false)
  : (feature: Features) =>
      (featuresDeployedToProd.has(feature) && !isDisabledForClientInProd(feature)) ||
      (featureEnablement[feature]?.has(getCurrentEnv() as Environments) ?? false);
